import React, { useEffect, useState } from 'react'
import { Select } from '../../components/ui/Select'
import { SearchField } from '../../components/ui/SearchField'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { TableEmptyRow, TableErrorRow, TableLoadingRow } from '../../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { useAuth } from '../../components/AuthProvider'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { safeJson } from '../../lib/utils'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import SupplierRiskHeatmap from '../../components/tpsm/SupplierRiskHeatmap'
import { pageScope } from '../../lib/cssScope'

type RiskRecord = {
  id: string
  supplierId: string
  supplier?: {
    id: string
    name: string
    email?: string
  }
  overallRisk?: number
  cyberRisk?: number
  financialRisk?: number
  operationalRisk?: number
  trendHistory?: Array<{
    date: string
    overallRisk: number
  }>
  createdAt: string
  updatedAt: string
}

function riskScorePillClass(risk: number | undefined, styles: typeof listStyles): string {
  if (risk === undefined || risk === null || Number.isNaN(risk)) {
    return `${styles.riskLevelPill} ${styles.riskLevelVeryLow}`
  }
  if (risk >= 70) return `${styles.riskLevelPill} ${styles.riskLevelCritical}`
  if (risk >= 40) return `${styles.riskLevelPill} ${styles.riskLevelMedium}`
  return `${styles.riskLevelPill} ${styles.riskLevelLow}`
}

function distributionTileClass(level: string, styles: typeof listStyles): string {
  const u = level.toUpperCase()
  if (u === 'HIGH') return `${styles.metricTile} ${styles.metricTileRed}`
  if (u === 'MEDIUM') return `${styles.metricTile} ${styles.metricTileYellow}`
  if (u === 'LOW') return `${styles.metricTile} ${styles.metricTileGreen}`
  return `${styles.metricTile} ${styles.metricTileBlue}`
}

const TPSMRiskScores: React.FC = () => {
  const { token } = useAuth()
  const { t } = useI18n()
  const [riskRecords, setRiskRecords] = useState<RiskRecord[]>([])
  const [summary, setSummary] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({
    riskLevel: 'all'
  })

  useEffect(() => {
    fetchRiskData()
  }, [token])

  const fetchRiskData = async () => {
    setLoading(true)
    try {
      const summaryRes = await fetch(API_ENDPOINTS.TPSM_RISK_SUMMARY, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const summaryData = await safeJson(summaryRes)
      setSummary(summaryData)

      const suppliersRes = await fetch(API_ENDPOINTS.SUPPLIERS, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const suppliersData = await safeJson(suppliersRes)
      const suppliers = Array.isArray(suppliersData.suppliers) ? suppliersData.suppliers : []

      const allRecords: RiskRecord[] = []
      for (const supplier of suppliers) {
        try {
          const recordRes = await fetch(API_ENDPOINTS.TPSM_RISK_SUPPLIER(supplier.id), {
            headers: { Authorization: `Bearer ${token}` }
          })
          const recordData = await safeJson(recordRes)
          if (Array.isArray(recordData) && recordData.length > 0) {
            allRecords.push(...recordData.map((r: any) => ({
              ...r,
              supplier: {
                id: supplier.id,
                name: supplier.name,
                email: supplier.email
              }
            })))
          }
        } catch {
          // Skip if no risk record exists
        }
      }
      setRiskRecords(allRecords)
    } catch (e: any) {
      setError(e.message || 'Failed to load risk data')
    } finally {
      setLoading(false)
    }
  }

  const getTrend = (record: RiskRecord) => {
    if (!record.trendHistory || record.trendHistory.length < 2) return null
    const sorted = [...record.trendHistory].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    )
    const latest = sorted[sorted.length - 1].overallRisk
    const previous = sorted[sorted.length - 2].overallRisk
    if (latest > previous) return 'up'
    if (latest < previous) return 'down'
    return 'stable'
  }

  const filteredRecords = riskRecords.filter(r => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      if (!r.supplier?.name?.toLowerCase().includes(query)) {
        return false
      }
    }

    if (filters.riskLevel !== 'all') {
      const overallRisk = r.overallRisk ?? 0
      if (filters.riskLevel === 'high' && overallRisk < 70) return false
      if (filters.riskLevel === 'medium' && (overallRisk < 40 || overallRisk >= 70)) return false
      if (filters.riskLevel === 'low' && overallRisk >= 40) return false
    }

    return true
  })

  const sortedRecords = [...filteredRecords].sort((a, b) => {
    const riskA = a.overallRisk ?? 0
    const riskB = b.overallRisk ?? 0
    return riskB - riskA
  })

  return (
    <div className={`${pageScope('tpsm-risk-scores')} ${listStyles.pageStack}`}>
      <GovernanceListingSection title={t('tpsm.risk.metricsTitle', 'Risk overview')}>
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.risk.totalSuppliers', 'Total Suppliers')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{summary?.totalSuppliers ?? 0}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTilePurple}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.risk.averageRisk', 'Average Risk')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textPurple}`}>{summary?.averageRisk ?? 0}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileRed}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.risk.highRisk', 'High Risk')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textRed}`}>{summary?.riskDistribution?.HIGH ?? 0}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileYellow}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.risk.mediumRisk', 'Medium Risk')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textYellow}`}>{summary?.riskDistribution?.MEDIUM ?? 0}</p>
          </div>
        </div>
      </GovernanceListingSection>

      <div className={listStyles.sectionSpacing}>
        <SupplierRiskHeatmap
          data={sortedRecords.map(record => ({
            supplierId: record.supplierId,
            supplierName: record.supplier?.name || 'Unknown',
            riskScore: record.overallRisk ?? null
          }))}
        />
      </div>

      {summary?.riskDistribution && (
        <GovernanceListingSection title={t('tpsm.risk.riskDistribution', 'Risk Distribution')}>
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
            {Object.entries(summary.riskDistribution).map(([level, count]: [string, any]) => (
              <div key={level} className={distributionTileClass(level, listStyles)}>
                <p className={listStyles.metricTileLabel}>{level}</p>
                <p className={`${listStyles.metricTileValue} ${listStyles.cellStrong}`}>{count}</p>
              </div>
            ))}
          </div>
        </GovernanceListingSection>
      )}

      <GovernanceListingSection
        title={t('tpsm.risk.filters', 'Filters')}
        toolbar={
          <div className={listStyles.panelToolbarFull}>
            <div className={listStyles.filtersRow}>
              <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
                <SearchField
                  label={t('tpsm.risk.searchLabel', 'Search')}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={t('tpsm.risk.searchPlaceholder', 'Search suppliers...')}
                  minWidth="14rem"
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('tpsm.risk.filterRiskLevel', 'Risk Level')}
                  options={[
                    { value: 'all', label: t('tpsm.risk.allRiskLevels', 'All Risk Levels') },
                    { value: 'high', label: t('tpsm.risk.high', 'High (70+)') },
                    { value: 'medium', label: t('tpsm.risk.medium', 'Medium (40-69)') },
                    { value: 'low', label: t('tpsm.risk.low', 'Low (<40)') }
                  ]}
                  value={filters.riskLevel || 'all'}
                  onChange={(v) => v != null && setFilters({ ...filters, riskLevel: v })}
                  minWidth="11rem"
                />
              </div>
            </div>
          </div>
        }
      >
        {null}
      </GovernanceListingSection>

      <GovernanceListingSection
        title={`${t('tpsm.risk.title', 'Supplier Risk Scores')} (${sortedRecords.length})`}
      >
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.risk.table.supplier', 'Supplier')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.risk.table.overallRisk', 'Overall Risk')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.risk.table.cyberRisk', 'Cyber Risk')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.risk.table.financialRisk', 'Financial Risk')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.risk.table.operationalRisk', 'Operational Risk')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.risk.table.trend', 'Trend')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.risk.table.updated', 'Updated')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableLoadingRow colSpan={7}>{t('common.loading', 'Loading...')}</TableLoadingRow>
              ) : error ? (
                <TableErrorRow colSpan={7}>{error}</TableErrorRow>
              ) : sortedRecords.length === 0 ? (
                <TableEmptyRow colSpan={7}>
                  {t('tpsm.risk.noRiskData', 'No risk data available')}
                </TableEmptyRow>
              ) : (
                sortedRecords.map((record) => {
                  const trend = getTrend(record)
                  return (
                    <tr key={record.id} className={listStyles.row}>
                      <td className={`${listStyles.td} ${listStyles.cellStrong}`}>
                        {record.supplier?.name || 'Unknown'}
                      </td>
                      <td className={listStyles.td}>
                        <span className={riskScorePillClass(record.overallRisk, listStyles)}>
                          {record.overallRisk ?? 'N/A'}
                        </span>
                      </td>
                      <td className={listStyles.td}>
                        <span className={riskScorePillClass(record.cyberRisk, listStyles)}>
                          {record.cyberRisk ?? 'N/A'}
                        </span>
                      </td>
                      <td className={listStyles.td}>
                        <span className={riskScorePillClass(record.financialRisk, listStyles)}>
                          {record.financialRisk ?? 'N/A'}
                        </span>
                      </td>
                      <td className={listStyles.td}>
                        <span className={riskScorePillClass(record.operationalRisk, listStyles)}>
                          {record.operationalRisk ?? 'N/A'}
                        </span>
                      </td>
                      <td className={listStyles.td}>
                        {trend === 'up' && <TrendingUp className={listStyles.badgeIcon} aria-hidden style={{ color: 'rgb(220 38 38)' }} />}
                        {trend === 'down' && <TrendingDown className={listStyles.badgeIcon} aria-hidden style={{ color: 'rgb(22 163 74)' }} />}
                        {trend === 'stable' && <Minus className={listStyles.badgeIcon} aria-hidden style={{ color: 'rgb(107 114 128)' }} />}
                        {!trend && <span className={listStyles.cellMuted}>—</span>}
                      </td>
                      <td className={`${listStyles.td} ${listStyles.cellMuted}`}>
                        {new Date(record.updatedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </GovernanceListingSection>
    </div>
  )
}

export default TPSMRiskScores
